/**
 * How Home flows at any width (SA-1406, spec 2026-10-26), pure. From the grid's measured width and the
 * visible cards, one stage for the whole page — lanes, narrow cards in pairs, or one column, widest first
 * — and the cards drawn narrower than their minimum, which show their headline. The most demanding
 * visible card decides the stage, even a wide widget set to narrow (owner's ruling). A tolerance band
 * keeps a scrollbar that comes and goes at a threshold from flipping the page.
 */
import type { HomeWidth } from '@/components/custom/homeWidgetTypes'
import { segments } from './homeLayout'

export type HomeStage = 'lanes' | 'pairs' | 'column'
/** Widest first. */
export const STAGES: HomeStage[] = ['lanes', 'pairs', 'column']
/** The grid's `gap-4`. */
export const HOME_GAP_PX = 16
/** How far past a threshold the page must grow before it widens a stage or a headline returns to full. */
export const HYSTERESIS_PX = 24

export interface FlowCard { id: string; width: HomeWidth; minPx: number }
export interface HomeFlow { stage: HomeStage; headlines: ReadonlySet<string> }

/** Before the grid is measured (and in a test with no layout): today's lanes, nothing shrunk. */
export const UNMEASURED: HomeFlow = { stage: 'lanes', headlines: new Set() }

const columns = (gridPx: number, gapPx: number) => {
  const col = (gridPx - 2 * gapPx) / 3
  return { col, main: 2 * col + gapPx, half: (gridPx - gapPx) / 2 }
}

function fits(stage: HomeStage, gridPx: number, cards: FlowCard[], gapPx: number): boolean {
  const { col, main, half } = columns(gridPx, gapPx)
  if (stage === 'lanes') return cards.every((c) => (c.width === 1 ? c.minPx <= col : c.width === 2 ? c.minPx <= main : true))
  if (stage === 'pairs') return cards.every((c) => c.width !== 1 || c.minPx <= half)
  return true
}

/** Each card's drawn width in a stage. In lanes a wide card in a run with no narrow card spans the page, as `drawItems` draws it. */
export function drawnWidths(gridPx: number, cards: FlowCard[], stage: HomeStage, gapPx = HOME_GAP_PX): Map<string, number> {
  const { col, main, half } = columns(gridPx, gapPx)
  const out = new Map<string, number>()
  if (stage === 'column') {
    for (const c of cards) out.set(c.id, gridPx)
    return out
  }
  if (stage === 'pairs') {
    for (const c of cards) out.set(c.id, c.width === 1 ? half : gridPx)
    return out
  }
  for (const seg of segments(cards)) {
    if (seg.kind === 'full') {
      out.set(seg.entry.id, gridPx)
      continue
    }
    const mixed = seg.entries.some((e) => e.width === 1)
    for (const e of seg.entries) out.set(e.id, e.width === 1 ? col : mixed ? main : gridPx)
  }
  return out
}

/** The stage and the headline cards at `gridPx`, given the flow drawn before (for the band). */
export function homeFlow(gridPx: number, cards: FlowCard[], previous: HomeFlow | null, gapPx = HOME_GAP_PX): HomeFlow {
  if (gridPx <= 0) return UNMEASURED
  const rank = (s: HomeStage) => STAGES.indexOf(s)
  const holds = (s: HomeStage) => fits(s, gridPx, cards, gapPx)
    && (previous === null || rank(s) >= rank(previous.stage) || fits(s, gridPx - HYSTERESIS_PX, cards, gapPx))
  const stage = STAGES.find(holds) ?? 'column'
  const drawn = drawnWidths(gridPx, cards, stage, gapPx)
  const headlines = new Set<string>()
  for (const c of cards) {
    // The stage's own check already fits these (narrow and wide in lanes, narrow in pairs), so the band can never
    // carry a headline into them after a large resize: only a card drawn at the grid's full width may need one.
    if ((stage === 'lanes' && c.width !== 3) || (stage === 'pairs' && c.width === 1)) continue
    const w = drawn.get(c.id) ?? gridPx
    const floor = previous?.headlines.has(c.id) ? c.minPx + HYSTERESIS_PX : c.minPx
    if (w < floor) headlines.add(c.id)
  }
  return { stage, headlines }
}

export function sameFlow(a: HomeFlow, b: HomeFlow): boolean {
  if (a.stage !== b.stage || a.headlines.size !== b.headlines.size) return false
  for (const id of a.headlines) if (!b.headlines.has(id)) return false
  return true
}

/** The grid per stage, each a whole string so Tailwind generates it (SA-1405). Dense flow only in pairs. */
export const GRID_CLASS: Record<HomeStage, string> = {
  lanes: 'grid grid-cols-3 items-start gap-4',
  pairs: 'grid grid-cols-2 items-start gap-4 [grid-auto-flow:dense]',
  column: 'grid grid-cols-1 items-start gap-4',
}

/** A card's span per stage and drawn width. */
export const SPAN_CLASS: Record<HomeStage, Record<HomeWidth, string>> = {
  lanes: { 1: '', 2: 'col-span-2', 3: 'col-span-3' },
  pairs: { 1: '', 2: 'col-span-2', 3: 'col-span-2' },
  column: { 1: '', 2: '', 3: '' },
}
